// D-237 (A4) — THE DIALOG BEHAVIOUR, EXTRACTED FROM `v3 Utils/Modal` AND FIXED ON THE WAY
// OUT.
//
// That component was the ONLY thing in `src/` with either an ESC handler or a Tab trap,
// and copying it to the three overlays that had nothing would have copied four defects
// with it. Each is fixed here and named where it is fixed:
//
//   1. the focusable list was SNAPSHOTTED ONCE, at arm time, so a dialog whose contents
//      change (v3's search modal renders results) traps against a stale list;
//   2. `release()` was defined and NEVER CALLED, and the element `keydown` listener was
//      never removed — `beforeUnmount` removed only the window ESC listener;
//   3. the selector omitted `[tabindex]`, `input[type="range"]` and `input[type="email"]`,
//      and a trap that skips ranges skips the widget's main control;
//   4. 🔴 it keyed on `mounted`, not on SHOWN.
//
// (4) is the one that makes this a mixin with lifecycle rather than a pure DOM function.
// `v3 PlayerInfo` renders `<share v-show=…>` and `Share` mounts `Utils/Modal`
// unconditionally, so ESC and the trap armed at PLAYER LOAD, the focusable snapshot was
// taken while the subtree was `display: none`, and nothing re-ran when the overlay
// appeared. `v3 HashTag/Modal` is class-toggled the same way. A trap that arms when the
// component mounts is, for two of the four overlays here, a trap that arms at the wrong
// time and never again.
//
// 🪤 AND A TRAP OVER ZERO FOCUSABLE ELEMENTS IS WORSE THAN NO TRAP. Before A2, `v4
// DescriptionModal` contained a bare `<svg>` close and a text div — nothing focusable at
// all — so `elements[0].focus()` was a no-op WITH THE TRAP LIVE: focus stayed behind the
// overlay and Tab walked the background. That is why A4 is blocked on A2, and why
// `focusFirst` below falls back to the container rather than doing nothing.

/**
 * What Tab may land on inside a dialog.
 *
 * 🪤 `[tabindex]` is included but `tabindex="-1"` is excluded, and the distinction is the
 * point: -1 means "programmatically focusable, NOT in the tab order", which is exactly
 * what the dialog container itself carries. Including it would make the container a stop
 * in its own cycle.
 *
 * `input` is unqualified rather than the original's list of five specific types, so
 * `range`, `email` and every type added later are covered by construction — the original
 * enumerated types and therefore silently skipped the scrubber.
 */
const FOCUSABLE = [
  'a[href]',
  'button:not([disabled])',
  'input:not([disabled]):not([type="hidden"])',
  'select:not([disabled])',
  'textarea:not([disabled])',
  '[tabindex]:not([tabindex="-1"])',
].join(', ');

const isEscape = (event) => event.key === 'Escape' || event.keyCode === 27;
const isTab = (event) => event.key === 'Tab' || event.keyCode === 9;

/**
 * Build the dialog mixin for one component.
 *
 * @param {object} o
 * @param {function(): boolean} o.shown called with the component as `this`; true while the
 *   dialog is on screen. For a `v-if`-mounted overlay that is simply `() => true`; for a
 *   `v-show`/class-toggled one it must read whatever actually toggles it.
 * @param {function(): void} o.close called with the component as `this` when ESC is
 *   pressed. Separate from `shown` because the three call sites close differently — two
 *   `$emit`, and `HashTag/Modal` invokes a function it was handed as a prop.
 * @param {function(): (Element|null)} [o.container] the element to trap within and to
 *   focus as a last resort. Defaults to `$refs.dialog`, then the component root.
 * @returns {object} a Vue options mixin
 */
export function createFocusTrap({ shown, close, container }) {
  return {
    computed: {
      // Named plainly rather than `_`-prefixed: Vue 3 reserves `_` and `$` on instance
      // properties, and a name that trips a framework warning in dev is a name someone
      // renames later without understanding why it was odd.
      trapShown() {
        return !!shown.call(this);
      },
    },
    watch: {
      trapShown(isShown) {
        if (isShown) this.armFocusTrap();
        else this.releaseFocusTrap();
      },
    },
    mounted() {
      // A `v-if`-mounted overlay is shown the instant it exists, and `watch` does not
      // fire for an initial value. Both entry points, one arm.
      if (this.trapShown) this.armFocusTrap();
    },
    beforeUnmount() {
      // 🔑 Defect (2). The original's `release()` existed and nothing called it, so the
      // element keydown listener outlived every modal the player ever opened.
      this.releaseFocusTrap();
    },
    methods: {
      trapContainer() {
        if (container) return container.call(this) || this.$el;
        return (this.$refs && this.$refs.dialog) || this.$el;
      },

      /**
       * Everything Tab may reach, RE-QUERIED. Defect (1): the original took this once, so
       * a dialog whose contents changed after opening trapped against a list that no
       * longer described it.
       *
       * @returns {Element[]}
       */
      trapFocusable() {
        const el = this.trapContainer();
        if (!el || typeof el.querySelectorAll !== 'function') return [];
        return Array.prototype.slice.call(el.querySelectorAll(FOCUSABLE));
      },

      armFocusTrap() {
        if (this.trapArmed) return;
        this.trapArmed = true;
        // Where focus came FROM, so it can go back. Nothing in this tree did this before,
        // and without it closing a dialog drops the user on `<body>` — the same defect
        // D-234 fixes for the play button, one interaction later.
        this.trapReturnFocusTo = typeof document !== 'undefined' ? document.activeElement : null;
        this.trapKeydown = (event) => this.onTrapKeydown(event);
        this.trapEscape = (event) => {
          if (isEscape(event)) close.call(this);
        };
        if (typeof window !== 'undefined') {
          window.addEventListener('keydown', this.trapEscape);
        }
        const el = this.trapContainer();
        if (el && el.addEventListener) el.addEventListener('keydown', this.trapKeydown);
        // Deferred a tick: for a `v-show` overlay the element is laid out in the same
        // patch that reveals it, and focusing a `display: none` element is a silent no-op
        // in every engine.
        this.$nextTick(() => {
          if (this.trapArmed) this.focusFirst();
        });
      },

      releaseFocusTrap() {
        if (!this.trapArmed) return;
        this.trapArmed = false;
        if (typeof window !== 'undefined' && this.trapEscape) {
          window.removeEventListener('keydown', this.trapEscape);
        }
        const el = this.trapContainer();
        if (el && el.removeEventListener && this.trapKeydown) {
          el.removeEventListener('keydown', this.trapKeydown);
        }
        this.trapEscape = null;
        this.trapKeydown = null;
        const back = this.trapReturnFocusTo;
        this.trapReturnFocusTo = null;
        // Guarded on still being in the document: the opener can legitimately have gone
        // away (an episode switch re-renders the list), and focusing a detached node
        // silently moves focus to `<body>`, which is the thing being avoided.
        if (back && typeof back.focus === 'function'
          && (typeof document === 'undefined' || document.contains(back))) {
          back.focus();
        }
      },

      focusFirst() {
        const focusable = this.trapFocusable();
        if (focusable.length) {
          focusable[0].focus();
          return;
        }
        // 🪤 The 0-focusable case. Doing nothing here leaves focus BEHIND a live trap,
        // where Tab walks the background and the user cannot get back — strictly worse
        // than not trapping at all. The container carries `tabindex="-1"` for this.
        const el = this.trapContainer();
        if (el && typeof el.focus === 'function') el.focus();
      },

      onTrapKeydown(event) {
        if (!isTab(event)) return;
        const focusable = this.trapFocusable();
        if (focusable.length < 2) {
          // One stop, or none: there is nowhere to cycle to, so hold focus where it is
          // rather than letting Tab escape into the background the dialog covers.
          event.preventDefault();
          this.focusFirst();
          return;
        }
        const first = focusable[0];
        const last = focusable[focusable.length - 1];
        const active = typeof document !== 'undefined' ? document.activeElement : null;
        if (event.shiftKey && active === first) {
          event.preventDefault();
          last.focus();
        } else if (!event.shiftKey && active === last) {
          event.preventDefault();
          first.focus();
        }
      },
    },
  };
}

export const FOCUSABLE_SELECTOR = FOCUSABLE;
